import { Severity } from 'Data/ViewModels/Core/types';
import { isListItemModel, ListItemModel } from 'Data/ViewModels/ListItemModel';
import { isNil } from 'ramda';
import React, { useCallback, useEffect } from 'react';
import { findDOMNode } from 'react-dom';
import { useViewport } from '../hooks';
import { IDropdownProps } from './Dropdown';
import { FilterOptionMethod } from './filters';

/**
 * Props common to dropdowns and creatable dropdowns
 */
export type ISharedDropdownProps = Pick<
  React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>,
  'disabled' | 'id' | 'name' | 'onChange' | 'onKeyDown' | 'placeholder'
> & {
  defaultValue?: string | ListItemModel | null | undefined;
  focusRef?: React.Ref<HTMLSelectElement>;
  isLoading?: boolean;
  maxResults?: number;
  onInputChange?: (event: any) => void;
  onMenuOpen?: (event?: any) => void;
  options?: ListItemModel[];
  severity?: Severity;
  simpleValue?: boolean; // https://github.com/JedWatson/react-select/issues/2669#issuecomment-427307588
  value?: string | ListItemModel | null | undefined;
  filterOption?: FilterOptionMethod;
};

/**
 * Add missing dropdown cursor support
 */
export const DropdownWrapper: React.FC<React.PropsWithChildren<{ disabled?: boolean }>> = ({
  children,
  disabled = false,
}) => <span style={disabled ? { cursor: 'not-allowed' } : undefined}>{children}</span>;

/**
 * Build a change handler to support simple values.
 */
export const useChangeHandler = ({ name, onChange, simpleValue }: ISharedDropdownProps) =>
  useCallback(
    (eventValue: any) => {
      const value = simpleValue ? eventValue && eventValue.value : eventValue;
      onChange?.({ data: { name, value, submitValues: true } } as any);
    },
    [name, onChange, simpleValue]
  );

/**
 * Apply the value in one of three ways:
 *
 * 1. if the value is an object with a value or label, use it directly
 * 2. if the value matches the value property in the list of options, use that value
 * 3. if all else fails, treat the value as both the value and label, so that we can see *something*
 */
export const toListItemValue = ({
  options,
  value,
  isMulti = false,
}: Pick<ISharedDropdownProps, 'options' | 'value'> & Pick<IDropdownProps, 'isMulti'>):
  | ListItemModel
  | ListItemModel[]
  | undefined
  | null => {
  // Multi-selects give an array of values
  if (Array.isArray(value)) {
    if (!isMulti) throw new Error("Can't use multiple dropdown values unless it is a multi-dropdown.");
    if (!value.every((v) => isListItemModel(v)))
      throw new Error("Can't use multiple dropdown unless all values are formatted correctly.");
    return value;
  }

  if (isNil(value)) return value;
  // Where value is provided as a list item model...
  //  * If it matches an item already in the list, return that item
  //  * If no matching option, return the list item value
  if (isListItemModel(value)) return options?.find((e) => e.value === value.value) ?? value;
  // Where value is provided directly (as a "simple value")...
  //  * If it matches an item already in the list, return that item
  //  * If no matching option, construct a list item where the label is just the value
  return options?.find((e) => e.value === value) ?? { value, label: value };
};

/**
 * An effect that will keep a dropdown menu positioned underneath a dropdown, based on the current viewport.
 */
export const useKeepMenuUnderDropdownEffect = (
  dropdownRef: React.MutableRefObject<any>,
  menuPortalRef: React.MutableRefObject<any>
) => {
  const viewport = useViewport();

  const handleScroll = useCallback(() => {
    if (dropdownRef.current && menuPortalRef.current) {
      // Creatable Dropdowns nest the select property
      const select = (dropdownRef as any).current;

      const {
        state: { placement },
      } = menuPortalRef.current;
      const dropdownBounds = select?.controlRef?.getBoundingClientRect();
      const menuPortalElement = findDOMNode(menuPortalRef.current) as any;

      if (dropdownBounds && menuPortalElement) {
        if (placement === 'top') {
          menuPortalElement.style.top = `${dropdownBounds.top}px`;
        } else {
          menuPortalElement.style.top = `${dropdownBounds.top + dropdownBounds.height}px`;
        }
      }
    }
  }, [dropdownRef, menuPortalRef]);

  return useEffect(() => {
    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      viewport.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, viewport]);
};
